import { Link, useNavigate } from '@tanstack/react-router';
import { CalendarClock, CheckCheck, Copy, Lock, Pencil, Play, Printer, Square, UserMinus, UserPlus, XCircle } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  ATTENDANCE_STATUS_LABELS,
  EXAM_SESSION_STATUS_LABELS,
  type ExamSessionView,
  type MarkableAttendance,
  type RosterRowView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card, MetricStrip } from '../../../components/Card';
import { Chip } from '../../../components/Chip';
import { DataTable } from '../../../components/data-table/DataTable';
import { ATTENDANCE_TONES, EXAM_SESSION_TONES, RESULT_TONES } from '../../../components/status-tones';
import { ToneChip } from '../../../components/Tone';
import type { Column, SortSpec } from '../../../components/data-table/types';
import { Input } from '../../../components/Input';
import { Popover } from '../../../components/Popover';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, NotFoundState, ServerErrorState } from '../../../components/system-states/SystemState';
import { anchorFrom, type AnchorRect } from '../../../lib/anchored-panel';
import { EMPTY, formatDateTime } from '../../../lib/format';
import { EMPTY_SELECTION } from '../../../lib/selection';
import { useRoom } from '../../../shell/SocketProvider';
import { useExamSession, useSessionCandidates } from '../api/exam-sessions-api';
import { AddPeoplePanel, CancelPanel, MovePanel, RemovePanel } from '../components/SessionPanels';
import { SessionEditor } from '../components/SessionEditor';
import { useSessionActions } from '../hooks/use-session-actions';
import styles from '../components/Assessments.module.css';

type Panel =
  | { readonly kind: 'move' | 'cancel' | 'add'; readonly anchor: AnchorRect }
  | { readonly kind: 'remove'; readonly anchor: AnchorRect; readonly row: RosterRowView };

const ATTENDANCE_CHOICES: readonly { readonly value: MarkableAttendance; readonly label: string }[] = [
  { value: 'present', label: 'Present' },
  { value: 'absent', label: 'No-show' },
  { value: 'rescheduled', label: 'Asked to move' },
];

/**
 * ASM-04 and ASM-05 — one exam session through its day (§L25): the list while it is
 * scheduled, attendance while the exam runs, then one mark per person (S18 Q1). N-26
 * lands here. An invigilator sees only their own sessions (QA test 3).
 */
export function ExamSessionPage({ sessionId }: { readonly sessionId: string }): ReactNode {
  const query = useExamSession(sessionId);
  const actions = useSessionActions(sessionId);
  const navigate = useNavigate();
  const [panel, setPanel] = useState<Panel | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [sort, setSort] = useState<readonly SortSpec[]>([]);
  const view = query.data;
  useRoom(view?.can.edit ? `job:${view.jobId}` : null);
  const candidates = useSessionCandidates(sessionId, panel?.kind === 'add');

  if (query.isPending) return <SkeletonLines count={10} />;
  if (query.error) {
    if (query.error.status === 403) return <ForbiddenState resource="this exam session" contact="The recruiter who set it" />;
    if (query.error.status === 404) return <NotFoundState onGoBack={() => void navigate({ to: '/assessments/sessions' })} />;
    return <ServerErrorState onRetry={() => void query.refetch()} />;
  }
  const session = query.data;
  const status = session.status;
  const isUpcoming = status === 'draft' || status === 'scheduled' || status === 'roster_locked';
  const open = (kind: 'move' | 'cancel' | 'add') => (event: MouseEvent<HTMLButtonElement>): void => setPanel({ kind, anchor: anchorFrom(event.currentTarget) });
  const count = (predicate: (row: RosterRowView) => boolean): number => session.roster.filter(predicate).length;
  const unmarked = session.roster.filter((row) => row.status === 'registered');

  const columns: readonly Column<RosterRowView>[] = [
    { id: 'seat', header: 'Seat', isNumeric: true, defaultWidth: 70, cell: (row) => row.seat },
    {
      id: 'name',
      header: 'Candidate',
      isIdentity: true,
      defaultWidth: 220,
      cell: (row) =>
        session.can.edit ? (
          <Link to="/applications/$applicationId" params={{ applicationId: row.applicationId }} className={styles.itemName}>
            {row.candidateName}
          </Link>
        ) : (
          row.candidateName
        ),
    },
    { id: 'phone', header: 'Phone', defaultWidth: 150, cell: (row) => row.phone ?? EMPTY },
    {
      id: 'status',
      header: 'Attendance',
      defaultWidth: status === 'in_progress' ? 330 : 150,
      cell: (row) =>
        status === 'in_progress' && session.can.runExam && row.status !== 'completed' ? (
          <div className={styles.itemActions} role="group" aria-label={`Attendance for ${row.candidateName}`}>
            {ATTENDANCE_CHOICES.map((choice) => (
              <Button
                key={choice.value}
                size="sm"
                variant={row.status === choice.value ? 'solid' : 'outline'}
                aria-pressed={row.status === choice.value}
                onClick={() => void actions.attendance([{ attendanceId: row.attendanceId, status: choice.value }])}
              >
                {choice.label}
              </Button>
            ))}
          </div>
        ) : (
          <ToneChip tone={ATTENDANCE_TONES[row.status]}>{ATTENDANCE_STATUS_LABELS[row.status]}</ToneChip>
        ),
    },
    {
      id: 'score',
      header: `Mark (of ${session.maxScore})`,
      isNumeric: true,
      defaultWidth: 190,
      cell: (row) =>
        status === 'results_entry' && session.can.enterResults && (row.status === 'present' || row.status === 'completed') ? (
          <ScoreInput row={row} maxScore={session.maxScore} onSave={(score) => void actions.result(row.attendanceId, score)} />
        ) : row.score === null ? null : (
          <span className={styles.score}>
            <strong>{row.score}</strong>
            {row.didPass === true && <Chip tone={RESULT_TONES.passed} plain>Passed</Chip>}
            {row.didPass === false && <Chip tone={RESULT_TONES.below} plain>Below</Chip>}
          </span>
        ),
    },
  ];

  const detailsLine = `${session.startsAt ? formatDateTime(session.startsAt) : 'No date yet'} · ${session.venue ?? 'No venue yet'} · invigilator ${session.invigilatorName ?? EMPTY} · ${session.testName}, out of ${session.maxScore}, pass mark ${session.passPercent}%`;

  return (
    <div className={styles.screen}>
      <Link to="/assessments/sessions" className={styles.back}>
        ← Exam sessions
      </Link>
      <Card
        title={session.name}
        meta={detailsLine}
      >
        <MetricStrip
          metrics={[
            { label: 'Booked', value: `${session.booked} of ${session.capacity}` },
            { label: 'Present', value: String(count((row) => row.status === 'present' || row.status === 'completed')) },
            { label: 'No-shows', value: String(count((row) => row.status === 'absent')) },
            { label: 'Marked', value: String(count((row) => row.score !== null)) },
            { label: 'Passed', value: String(count((row) => row.didPass === true)) },
          ]}
        />
        {session.note && <p className={styles.notice}>{session.note}</p>}
        <StageHint session={session} />
      </Card>

      <div className={styles.actionsRow}>
        <ToneChip tone={EXAM_SESSION_TONES[status]}>{EXAM_SESSION_STATUS_LABELS[status]}</ToneChip>
        {session.can.edit && status === 'scheduled' && (
          <Button variant="outline" icon={UserPlus} disabled={session.booked >= session.capacity} onClick={open('add')}>
            Add people
          </Button>
        )}
        {session.can.edit && status === 'scheduled' && (
          <Button variant="ghost" icon={Lock} onClick={() => void actions.step(session, 'lock')}>
            Lock list
          </Button>
        )}
        {session.can.runExam && (status === 'scheduled' || status === 'roster_locked') && session.booked > 0 && (
          <Button variant="solid" icon={Play} onClick={() => void actions.step(session, 'start')}>
            Start exam
          </Button>
        )}
        {session.can.runExam && status === 'in_progress' && unmarked.length > 0 && (
          <Button variant="outline" icon={CheckCheck} onClick={() => void actions.attendance(unmarked.map((row) => ({ attendanceId: row.attendanceId, status: 'present' as const })))}>
            Mark the other {unmarked.length} present
          </Button>
        )}
        {session.can.runExam && status === 'in_progress' && (
          <Button variant="solid" icon={Square} disabled={unmarked.length > 0} onClick={() => void actions.step(session, 'finish')}>
            Finish exam, enter marks
          </Button>
        )}
        {session.can.edit && status === 'results_entry' && (
          <Button variant="solid" icon={CheckCheck} disabled={count((row) => row.status === 'present') > 0} onClick={() => void actions.step(session, 'complete')}>
            Complete session
          </Button>
        )}
        {status !== 'draft' && status !== 'cancelled' && (
          <Button variant="ghost" icon={Printer} onClick={() => void navigate({ to: '/assessments/sessions/$sessionId/print', params: { sessionId } })}>
            Print list
          </Button>
        )}
        {session.can.edit && isUpcoming && (
          <Button variant="ghost" icon={Pencil} onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
        {session.can.edit && (status === 'scheduled' || status === 'roster_locked') && (
          <Button variant="ghost" icon={CalendarClock} onClick={open('move')}>
            Move
          </Button>
        )}
        {session.can.edit && isUpcoming && (
          <Button variant="ghost" icon={XCircle} onClick={open('cancel')}>
            Cancel
          </Button>
        )}
      </div>

      <DataTable
        screenId="exam-session-roster"
        label="Candidates on this exam"
        columns={columns}
        rows={session.roster}
        toneOf={(row) => ATTENDANCE_TONES[row.status]}
        getRowId={(row) => row.attendanceId}
        total={session.roster.length}
        sort={sort}
        onSortChange={setSort}
        selection={EMPTY_SELECTION}
        onSelectionChange={() => undefined}
        isSelectable={false}
        rowActions={
          session.can.edit && (isUpcoming || status === 'cancelled')
            ? (row) => (
                <>
                  <Button variant="quiet" size="sm" icon={Copy} onClick={() => void actions.copy(row.attendanceId, row.candidateName)}>
                    Copy message
                  </Button>
                  {(status === 'scheduled' || status === 'roster_locked') && row.status === 'registered' && (
                    <Button variant="quiet" size="sm" icon={UserMinus} aria-label={`Take ${row.candidateName} off`} onClick={(event: MouseEvent<HTMLButtonElement>) => setPanel({ kind: 'remove', anchor: anchorFrom(event.currentTarget), row })} />
                  )}
                </>
              )
            : undefined
        }
        emptyState={<p className={styles.pageHint}>{session.can.edit && status === 'scheduled' ? 'Nobody is booked yet. In this job’s Contact step, select the people who confirmed and use "Add to exam session".' : 'Nobody is booked on this session.'}</p>}
      />

      <Popover anchor={panel?.anchor ?? null} onClose={() => setPanel(null)} align="right" width={420} label="Exam session">
        {panel?.kind === 'move' && (
          <MovePanel
            session={session}
            onCancel={() => setPanel(null)}
            onMove={(startsAt, venue) => {
              setPanel(null);
              void actions.reschedule(session, startsAt, venue);
            }}
          />
        )}
        {panel?.kind === 'cancel' && (
          <CancelPanel
            session={session}
            onCancel={() => setPanel(null)}
            onConfirm={(reason) => {
              setPanel(null);
              void actions.cancel(session, reason);
            }}
          />
        )}
        {panel?.kind === 'add' && (
          <AddPeoplePanel
            options={candidates.data ?? []}
            isLoading={candidates.isPending}
            seatsLeft={session.capacity - session.booked}
            onCancel={() => setPanel(null)}
            onAdd={(ids) => {
              setPanel(null);
              void actions.add(ids);
            }}
          />
        )}
        {panel?.kind === 'remove' && (
          <RemovePanel
            name={panel.row.candidateName}
            onCancel={() => setPanel(null)}
            onConfirm={() => {
              const row = panel.row;
              setPanel(null);
              void actions.remove(row.attendanceId, row.candidateName);
            }}
          />
        )}
      </Popover>
      <SessionEditor isOpen={isEditing} onClose={() => setIsEditing(false)} session={session} />
    </div>
  );
}

/** What happens next, in a sentence, so nobody has to know §L25. */
function StageHint({ session }: { readonly session: ExamSessionView }): ReactNode {
  const hints: Partial<Record<ExamSessionView['status'], string>> = {
    draft: 'A draft. It is scheduled once it has a date, a venue and an invigilator.',
    scheduled: session.isShortNotice
      ? 'Less than a day away, so the list stays open until you lock it or the exam starts.'
      : 'The list locks by itself a day before the exam, when everyone booked is reminded.',
    roster_locked: 'The list is locked. On the day, the invigilator starts the exam and takes attendance.',
    in_progress: 'Mark each person present, a no-show, or asked to move. A no-show is never a fail.',
    results_entry: 'Type each mark and press Enter. Someone who did not sit has no mark.',
    completed: 'Marks are on the job’s Assessment tab. Passes moved on by themselves where the job says so.',
    cancelled: session.statusReason ? `Cancelled: ${session.statusReason}` : 'Cancelled.',
  };
  const hint = hints[session.status];
  return hint ? <p className={styles.hint}>{hint}</p> : null;
}

/** One total per person (S18 Q1). Saved on Enter or on leaving the box; the row shows it at once. */
function ScoreInput({ row, maxScore, onSave }: { readonly row: RosterRowView; readonly maxScore: number; readonly onSave: (score: number) => void }): ReactNode {
  const [text, setText] = useState(row.score === null ? '' : String(row.score));
  const value = text.trim() === '' ? null : Number(text);
  const isInvalid = value !== null && (Number.isNaN(value) || value < 0 || value > maxScore);

  function commit(): void {
    if (value === null || isInvalid || value === row.score) return;
    onSave(Math.round(value * 100) / 100);
  }

  return (
    <span className={styles.score}>
      <Input
        aria-label={`Mark for ${row.candidateName}`}
        isNumeric
        inputMode="decimal"
        isInvalid={isInvalid}
        value={text}
        suffix={`/ ${maxScore}`}
        onChange={(event) => setText(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit();
        }}
      />
      {row.didPass === true && <Chip tone={RESULT_TONES.passed} plain>Passed</Chip>}
      {row.didPass === false && <Chip tone={RESULT_TONES.below} plain>Below</Chip>}
    </span>
  );
}
